<template>
  <div
    v-if="orderData"
    class="plv-iar-order-detail"
  >
    <div class="plv-iar-order-detail__wrap">
      <!-- 订单状态标题 -->
      <div class="plv-iar-order-detail__top">
        <!-- 订单状态 -->
        <span class="plv-iar-order-detail__top__title">
          {{ iarTrans(`orderList.status.${orderData.status}`) }}
        </span>
        <!-- 订单状态详细说明 -->
        <div
          class="plv-iar-order-detail__top__tips"
        >
          <template
            v-if="isWaitPay"
          >
            <div v-if="_useLang === 'en'">
              <span>{{ iarTrans('orderDetail.payAutoCancel') }}</span>
              <pay-time
                :end-time="orderData.expireTime"
                @count-down-end="getOrderDetail"
              />
            </div>
            <div v-else>
              <pay-time
                :end-time="orderData.expireTime"
                @count-down-end="getOrderDetail"
              />
              <span>{{ iarTrans('orderDetail.payAutoCancel') }}</span>
            </div>
          </template>
          <template
            v-else-if="isClose"
          >
            <span>{{ iarTrans('orderDetail.cancelReason') }}</span>
            <span>: </span>
            <span>{{ iarTrans('orderDetail.payTimeOut') }}</span>
          </template>
        </div>
      </div>

      <!-- 订单详细内容 -->
      <div class="plv-iar-order-detail__middle">
        <!-- 渠道码 (目前按订单仅有一个商品处理) -->
        <div
          v-if="hasCustomerService"
          class="plv-iar-order-detail__middle__item plv-iar-order-detail__middle__customerService"
        >
          <div class="plv-iar-order-detail__middle__customerService__img__wrap">
            <template v-if="customerServiceContact">
              <img
                class="plv-iar-order-detail__middle__customerService__img"
                :src="customerServiceContact"
              />
            </template>

            <template
              v-else
            >
              <div class="plv-iar-order-detail__middle__customerService__img plv-iar-order-detail__middle__customerService__img--expired" ></div>
              <span
              >
                {{ iarTrans('orderDetail.expired') }}
              </span>
            </template>
          </div>
          <span>{{ customerServiceTips }}</span>
        </div>

        <!-- 物流信息 -->
        <div
          v-if="orderData.expressEnabled === 'Y'"
          class="plv-iar-order-detail__middle__item plv-iar-order-detail__middle__addressExt plv-iar-order-detail__middle__addressExt--express"
        >
          <div>
            <span>{{ orderData.addressExt.name }}</span>
            <span class="plv-iar-order-detail__middle__addressExt--express__mobile">{{ orderData.addressExt.mobile | formatMobileNumber(true) }}</span>
          </div>
          <div class="plv-iar-order-detail__middle__addressExt--express__address">{{ orderData.addressExt.address }}</div>
        </div>

        <!-- 手机号 -->
        <div
          v-else
          class="plv-iar-order-detail__middle__item plv-iar-order-detail__middle__addressExt plv-iar-order-detail__middle__addressExt--virtual"
        >
          <span>{{ iarTrans('orderDetail.mobile') }}</span>
          <span>:</span>
          <span>{{ orderData.addressExt.mobile | formatMobileNumber(true) }}</span>
        </div>

        <div
          class="plv-iar-order-detail__middle__item plv-iar-order-detail__middle__detail"
        >
          <product-card
            v-for="product in orderData.products"
            :key="product.id"
            :data-source="product"
            in-order-theme
          />

          <!-- 支付方式 -->
          <payment-methods
            v-if="isWaitPay && showPaymentMethod"
            v-model="paymentMethod"
          />

          <!-- 订单号 -->
          <div
            class="plv-iar-order-detail__middle__detail__tips"
          >
            <span>{{ iarTrans('orderDetail.orderNo') }}</span>
            <span>{{ orderData.orderNo }}</span>
          </div>

          <!-- 下单时间 -->
          <div
            class="plv-iar-order-detail__middle__detail__tips"
          >
            <span>{{ iarTrans('orderDetail.createTime') }}</span>
            <span>{{ formatTimeStamp(orderData.createTime) }}</span>
          </div>

          <!-- 支付时间 -->
          <div
            v-if="!isWaitPay && !isClose && orderData.payTime"
            class="plv-iar-order-detail__middle__detail__tips"
          >
            <span>{{ iarTrans('orderDetail.payTime') }}</span>
            <span>{{ formatTimeStamp(orderData.payTime) }}</span>
          </div>

          <!-- 发货时间 -->
          <div
            v-if="(isFinish || isDelivering) && orderData.deliveryTime && !this.isVirtual"
            class="plv-iar-order-detail__middle__detail__tips"
          >
            <span>{{ iarTrans('orderDetail.deliveryTime') }}</span>
            <span>{{ formatTimeStamp(orderData.deliveryTime) }}</span>
          </div>

          <!-- 完成时间 -->
          <div
            v-if="isFinish && orderData.finishTime"
            class="plv-iar-order-detail__middle__detail__tips"
          >
            <span>{{ iarTrans('orderDetail.finishTime') }}</span>
            <span>{{ formatTimeStamp(orderData.finishTime) }}</span>
          </div>

          <!-- 实付金额 -->
          <div
            v-if="!isWaitPay && !isClose && orderData.amount"
            class="plv-iar-order-detail__middle__detail__tips"
          >
            <span>{{ iarTrans('orderDetail.totalAmount') }}</span>
            <div class="plv-iar-order-detail__middle__detail__tips__amount">
              <span>￥</span>
              <span>{{ orderData.amount }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部金额显示 & 按钮-->
    <div
      class="plv-iar-order-detail__bottom"
      :class="{
        'plv-iar-order-detail__bottom--content': isWaitPay || isDelivering
      }"
    >
      <!-- 待支付 -->
      <div
        v-if="isWaitPay"
        class="plv-iar-order-detail__bottom__item"
      >
        <div>
          <span>{{ iarTrans('orderDetail.totalAmount') }}</span>
          <span>￥</span>
          <span>{{ orderData.amount }}</span>
        </div>
        <span
          class="plv-iar-order-detail__bottom__btn"
          @click="handleOrderBtnClick"
        >
          {{ iarTrans('orderDetail.payNow') }}
        </span>
      </div>

      <!-- 待收货 -->
      <div
        v-if="isDelivering"
        class="plv-iar-order-detail__bottom__item"
      >
        <span
          class="plv-iar-order-detail__bottom__btn"
          @click="handleOrderBtnClick"
        >{{ iarTrans('orderDetail.confirmReceipt') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import orderDetailMixin from './order-detail-mixin';
import ProductCard from '../../components/order/ProductCard';
import PayTime from '../../components/order/PaySurplusTime';
import { formatMobileNumber } from '../../assets/utils/utils';
import PaymentMethods from '../../components/order/PaymentMethods';

export default {
  mixins: [orderDetailMixin],

  components: {
    ProductCard,
    PayTime,
    PaymentMethods
  },

  filters: {
    formatMobileNumber
  },
};

</script>

<style lang="scss">
.plv-iar-order-detail {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: #1D2129;
  height: 100%;
  line-height: 1.2;
}

.plv-iar-order-detail__wrap {
  flex: 1;
  overflow: auto;
}

.plv-iar-order-detail__top {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.plv-iar-order-detail__top__title {
  font-size: 20px;
  font-weight: 500;
}

.plv-iar-order-detail__top__tips {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999999;
  margin-top: 6px;
}

.plv-iar-order-detail__middle {
  padding: 14px 12px 12px 12px;
}

.plv-iar-order-detail__middle__item {
  background: #fff;
  margin-top: 8px;
}

.plv-iar-order-detail__middle__customerService {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  border-radius: 8px;

  > span {
    margin-top: 12px;
    color: rgba(#000, 0.4);
  }
}

.plv-iar-order-detail__middle__customerService__img__wrap {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #CCCCCC;
  border-radius: 4px;
  padding: 10px;

  span {
    position: absolute;
    opacity: 0.4;
  }
}

.plv-iar-order-detail__middle__customerService__img {
  width: 120px;
  height: 120px;
}

.plv-iar-order-detail__middle__customerService__img--expired {
  background: url('./imgs/icon-qr-expired.png') center / 100% no-repeat;
}

.plv-iar-order-detail__middle__addressExt {
  padding: 16px 12px;
  border-radius: 8px;
}

.plv-iar-order-detail__middle__addressExt--express {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .plv-iar-order-detail__middle__addressExt--express__mobile {
    margin-left: 4px;
  }

  .plv-iar-order-detail__middle__addressExt--express__address {
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    white-space: normal;
    margin-top: 6px;
    color: rgba(#000000, 0.4);
  }
}

.plv-iar-order-detail__middle__addressExt--virtual {
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span:nth-child(3) {
    margin-left: 12px;
  }
}

.plv-iar-order-detail__middle__detail {
  border-radius: 8px;
}

.plv-iar-order-detail__middle__detail__tips {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;

  > span:first-child {
    color: rgba(#000, 0.4);
  }
}

.plv-iar-order-detail__middle__detail__tips__amount {
  color: #F15D5D;
  span:first-child {
    font-size: 12px;
  }
  span:last-child {
    font-size: 18px;
  }
}

.plv-iar-order-detail__bottom {
  box-sizing: border-box;
}

.plv-iar-order-detail__bottom--content {
  border-top: 1px solid #E6E6E6;
  background: #fff;
}

.plv-iar-order-detail__bottom__item {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 12px;
}

.plv-iar-order-detail__bottom__btn {
  color: #fff;
  background: #F15D5D;
  padding: 8px 24px;
  border-radius: 20px;
  margin-left: auto;
  font-size: 14px;
}

</style>
